import React, { useRef, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { MailOutline, SearchOff, ArrowForward, ArrowBack } from '@mui/icons-material';
import { MailMessageSummary } from '../types';
import { MAIL_COLORS } from '../constants';
import MailListItem from './MailListItem';

interface MailListProps {
  messages: MailMessageSummary[];
  selectedMessageId: string | null;
  isLoading: boolean;
  hasFilterActive: boolean;
  page?: number;
  hasMore?: boolean;
  pageSize?: number;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  onSelectMessage: (msg: MailMessageSummary) => void;
}

export const MailList: React.FC<MailListProps> = ({
  messages,
  selectedMessageId,
  isLoading,
  hasFilterActive,
  page = 0,
  hasMore,
  pageSize = 50,
  onPrevPage,
  onNextPage,
  onSelectMessage
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Автоматический скролл наверх списка при переходе между страницами
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [page]);
  if (isLoading && messages.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          py: 8,
          gap: 2
        }}
      >
        <CircularProgress size={32} sx={{ color: MAIL_COLORS.primary }} />
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          Загрузка писем...
        </Typography>
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          py: 10,
          px: 3,
          textAlign: 'center',
          gap: 1.5
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            bgcolor: '#f1f5f9',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#94a3b8'
          }}
        >
          {hasFilterActive ? (
            <SearchOff sx={{ fontSize: 28 }} />
          ) : (
            <MailOutline sx={{ fontSize: 28 }} />
          )}
        </Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#334155' }}>
          {hasFilterActive ? 'Ничего не найдено' : 'В этой папке нет писем'}
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8', maxWidth: 300 }}>
          {hasFilterActive
            ? 'Попробуйте изменить поисковый запрос или сбросить фильтр'
            : 'Новые входящие сообщения будут отображаться здесь'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        overflowY: 'auto',
        height: '100%',
        bgcolor: '#ffffff'
      }}
    >
      {messages.map((message) => (
        <MailListItem
          key={message.id}
          message={message}
          isSelected={selectedMessageId === message.id}
          onClick={() => onSelectMessage(message)}
        />
      ))}

      {(page > 0 || hasMore) && (
        <Box
          sx={{
            p: 2,
            px: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            bgcolor: '#f8fafc',
            borderTop: `1px solid ${MAIL_COLORS.borderLight}`
          }}
        >
          {page > 0 && onPrevPage ? (
            <Button
              variant="outlined"
              size="small"
              onClick={onPrevPage}
              disabled={isLoading}
              startIcon={<ArrowBack sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontSize: '0.82rem',
                fontWeight: 600,
                color: '#334155',
                borderColor: '#cbd5e1',
                bgcolor: '#ffffff',
                '&:hover': { bgcolor: '#f1f5f9', borderColor: '#94a3b8' }
              }}
            >
              Предыдущая страница
            </Button>
          ) : (
            <Box />
          )}

          {hasMore && onNextPage && (
            <Button
              variant="contained"
              size="small"
              onClick={onNextPage}
              disabled={isLoading}
              endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
              sx={{
                borderRadius: '10px',
                textTransform: 'none',
                fontSize: '0.82rem',
                fontWeight: 600,
                bgcolor: MAIL_COLORS.primary,
                boxShadow: 'none',
                '&:hover': { bgcolor: MAIL_COLORS.primaryHover, boxShadow: 'none' }
              }}
            >
              Следующая страница
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MailList;
